import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PhysicianAccountPgpsDTO, ProductNames, ProviderAccountPgpDTO, ProviderAccountPgpsDTO, ProviderPgpDTO } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { SubjectOptions } from '@app/products/pac/models/additional-info-fax-subject.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toTenDigitFaxNumber } from '@shared/utility';

enum ProviderTypes {
  service = 1,
  order = 2
}

@Component({
    selector: 'app-fax-provider-details',
    templateUrl: './fax-provider-details.component.html',
    styleUrls: ['./fax-provider-details.component.scss'],
    standalone: false
})
export class FaxProviderDetailsComponent implements OnInit, OnChanges {
  @Input() servicingFacilityData!: ProviderAccountPgpsDTO & ProviderAccountPgpDTO;

  @Input() orderingProviderData!: PhysicianAccountPgpsDTO;

  @Input() serviceProviderLabel = 'Service Facility';

  @Input() orderingProviderLabel = 'Referral Provider';

  @Input() subject = '';

  @Input() recipientName = '';

  @Input() locationFaxConfirmed = true;

  @Input() isPdp: boolean;

  @Output() outboundModel = new EventEmitter<any>(); // OutboundFaxDTO>();

  @Output() isValid = new EventEmitter<boolean>();
  @Output() getProviderType = new EventEmitter<any>();
  @Input() productName = '';
  @Output() faxformValues = new EventEmitter<any>();

  providerTypes = ProviderTypes;
  faxForm!: FormGroup;
  subjectOptions: SelectItem[] = [
    { value: SubjectOptions.REQUEST_FOR_ADDITIONAL_INFORMATION, label: SubjectOptions.REQUEST_FOR_ADDITIONAL_INFORMATION },
    { value: SubjectOptions.POTENTIAL_ADVERSE_DETERMINATION, label: SubjectOptions.POTENTIAL_ADVERSE_DETERMINATION },
    { value: SubjectOptions.OTHER, label: SubjectOptions.OTHER }
  ]

  private readonly destroyRef = inject(DestroyRef);


  ngOnInit() {
    this.initForm();
    this.formChanges();
    this.providerTypeChange();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['servicingFacilityData'] && changes['servicingFacilityData'].firstChange) {
      setTimeout(() => this.setProviderAddress(), 0);
    }
  }

  get providerLocation() {
    return this.faxForm.get('providerLocation');
  }

  get recipientFaxNumber() {
    return this.faxForm.get('recipientFaxNumber');
  }

  initForm() {
    this.faxForm = new FormGroup({
      providerType: new FormControl(ProviderTypes.service, Validators.required),
      recipientName: new FormControl(this.recipientName, [Validators.required, this.noWhitespaceValidator, Validators.maxLength(250)]),
      subject: new FormControl(this.subject, [Validators.required, this.noWhitespaceValidator, Validators.maxLength(100)]),
      providerLocation: new FormControl('', [Validators.required, this.noWhitespaceValidator, Validators.maxLength(100)]),
      recipientFaxNumber: new FormControl('', [Validators.required, Validators.minLength(10)]),
    });
    this.setSubjectField() ? this.faxForm.get('subject').valueChanges
      .subscribe(value => this.emitSubjectValue()) : '';
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }

  providerTypeChange(): void {
    this.getProviderType.emit(this.faxForm.get('providerType').value);
    this.faxForm.get('providerType')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(type => {
      if (type === ProviderTypes.service) {
        this.setProviderAddress();
      }
      this.getProviderType.emit(type);
      if (type === ProviderTypes.order) {
        if (this.orderingProviderData) {
          this.providerLocation?.setValue(this.orderingProviderData.physicianAccountDTO.accountName || '');
          this.recipientFaxNumber?.setValue('');
        } else {
          this.providerLocation?.setValue('');
          this.recipientFaxNumber?.setValue('');
        }
      }
    });
  }

  formChanges(): void {
    this.faxForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.faxForm.valid) {
        this.isValid.emit(true);
        this.outboundModel.emit(this.faxForm.value);
      } else {
        this.isValid.emit(false);
      }
    });
  }

  reset() {
    this.faxForm.reset({ providerType: ProviderTypes.service });
    this.setProviderAddress();
  }

  setSubjectField() : boolean{
    if(this.productName == ProductNames.HomeHealth && this.subject == 'Request For Additional Information'){
       return true;
    }
    return false;
  }

  emitSubjectValue(){
    this.faxformValues.emit(this.faxForm);
  }

  private setProviderAddress() {
    if (this.servicingFacilityData?.providerAccountDTO) {
      const { providerAccountDTO } = this.servicingFacilityData;
      const providerPgpDTO = this.servicingFacilityData.providerPgpDTOList?.[0] || this.servicingFacilityData.providerPgpDTO || {} as ProviderPgpDTO;
      const providerLocation = providerAccountDTO.providerName || '';

      let recipientFaxNumber = '';

      if (providerPgpDTO) {
        if (providerAccountDTO.locationFaxConfirmed) {
          recipientFaxNumber = providerPgpDTO.authorizationFaxConfirmed ? providerPgpDTO.authorizationFax : providerAccountDTO.locationFax;
        } else {
          recipientFaxNumber = providerPgpDTO.authorizationFax;
        }
      }
      if (this.locationFaxConfirmed) {
        this.providerLocation?.setValue(providerLocation);
      }
      this.recipientFaxNumber?.setValue(toTenDigitFaxNumber(recipientFaxNumber));
    } else {
      this.providerLocation?.setValue('');
      this.recipientFaxNumber?.setValue('');
    }
  }
}
